import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

async function getServiceWorkerRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  return navigator.serviceWorker.register("/sw.js", { scope: "/" });
}

async function fetchVapidPublicKey() {
  const { data } = await axios.get(`${API_BASE}/api/push/vapid-public-key`);
  return data?.publicKey;
}

export async function subscribeToPushNotifications(token, { requestPermission = true } = {}) {
  if (!isPushSupported() || !token) {
    return { success: false, reason: "unsupported" };
  }

  let permission = Notification.permission;
  if (permission !== "granted") {
    if (!requestPermission || permission === "denied") {
      return { success: false, reason: "permission_denied" };
    }
    permission = await Notification.requestPermission();
  }

  if (permission !== "granted") {
    return { success: false, reason: "permission_denied" };
  }

  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    return { success: false, reason: "no_service_worker" };
  }

  await navigator.serviceWorker.ready;

  const publicKey = await fetchVapidPublicKey();
  if (!publicKey) {
    return { success: false, reason: "missing_vapid_key" };
  }

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  await axios.post(
    `${API_BASE}/api/push/subscribe`,
    {
      subscription: subscription.toJSON(),
      userAgent: navigator.userAgent,
    },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  return { success: true, subscription };
}

export async function unsubscribeFromPushNotifications(token) {
  if (!isPushSupported()) {
    return { success: false, reason: "unsupported" };
  }

  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = registration
    ? await registration.pushManager.getSubscription()
    : null;

  if (subscription) {
    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    if (token) {
      try {
        await axios.post(
          `${API_BASE}/api/push/unsubscribe`,
          { endpoint },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.warn("Failed to remove push subscription on server:", err);
      }
    }
  } else if (token) {
    try {
      await axios.post(
        `${API_BASE}/api/push/unsubscribe`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.warn("Failed to clear push subscriptions on server:", err);
    }
  }

  return { success: true };
}

export async function syncPushSubscription(
  enabled,
  token,
  { requestPermission = false } = {}
) {
  if (!token) return { success: false, reason: "no_token" };
  try {
    if (enabled) {
      return await subscribeToPushNotifications(token, { requestPermission });
    }
    return await unsubscribeFromPushNotifications(token);
  } catch (err) {
    console.error("Push subscription sync failed:", err);
    return { success: false, reason: err?.message || "sync_failed" };
  }
}
